import React, { useEffect, useMemo, useState } from 'react';
import {
  listStakeholders, deleteStakeholder,
  stakeholderInitials, groupStakeholdersByType,
} from '../data/stakeholders';
import StakeholderModal from './StakeholderModal';
import './Stakeholders.css';

function StakeholderCardSkeleton() {
  return (
    <div className="sh-card sh-skelcard" aria-hidden="true">
      <div className="sh-card-top">
        <span className="rp-skel sh-skel-avatar" />
        <div className="sh-card-id">
          <div className="rp-skel" style={{ width: '60%', height: 14, marginBottom: 7 }} />
          <div className="rp-skel" style={{ width: 64, height: 16, borderRadius: 999 }} />
        </div>
      </div>
      <div className="rp-skel" style={{ width: '45%', height: 12 }} />
      <div className="rp-skel" style={{ width: '80%', height: 12 }} />
      <div className="rp-skel" style={{ width: '55%', height: 12 }} />
    </div>
  );
}

function StakeholderCard({ s, onEdit, onDelete }) {
  const primary = (s.contacts || []).find((c) => c.is_primary) || (s.contacts || [])[0];
  return (
    <article className="sh-card">
      <div className="sh-card-top">
        <span className="sh-avatar">
          {s.logo ? <img src={s.logo} alt="" /> : stakeholderInitials(s.name)}
        </span>
        <div className="sh-card-id">
          <h3>{s.name}</h3>
          {s.role && <span className="sh-card-roletag">{s.role}</span>}
        </div>
        {/* type is conveyed by the section heading, so it's dropped from the card */}
        <div className="sh-card-actions">
          <button type="button" aria-label="עריכה" onClick={() => onEdit(s)}><i className="fi fi-rr-edit" /></button>
          <button type="button" aria-label="מחיקה" className="is-danger" onClick={() => onDelete(s)}><i className="fi fi-rr-trash" /></button>
        </div>
      </div>
      <div className="sh-card-meta">
        {s.email && <span><i className="fi fi-rr-envelope" /><span className="sh-ltr">{s.email}</span></span>}
        {s.phone && <span><i className="fi fi-rr-phone-call" /><span className="sh-ltr">{s.phone}</span></span>}
      </div>
      {primary && (
        <div className="sh-card-contact">
          <i className="fi fi-rr-user" />
          <span>{[primary.name, primary.role].filter(Boolean).join(' · ')}</span>
          {(s.contacts || []).length > 1 && <span className="sh-more">+{s.contacts.length - 1}</span>}
        </div>
      )}
    </article>
  );
}

export default function Stakeholders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null = closed; {} = new; obj = edit

  const reload = () => {
    setLoading(true);
    return listStakeholders()
      .then((rows) => { setItems(rows); setError(''); })
      .catch((e) => setError(e.message || 'טעינת בעלי העניין נכשלה.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim();
    if (!q) return items;
    return items.filter((s) => `${s.name} ${s.role} ${s.type} ${s.email}`.includes(q));
  }, [items, search]);

  const handleDelete = async (s) => {
    await deleteStakeholder(s.id).catch(() => {});
    reload();
  };

  return (
    <div className="sh">
      <header className="sh-head">
        <div className="sh-head-text">
          <h1>בעלי עניין</h1>
          <p>ניהול בעלי עניין משותפים לכלל הפרויקטים — יזמים, אדריכלים, יועצים ועוד.</p>
        </div>
        <div className="sh-head-tools">
          <div className="sh-search">
            <i className="fi fi-rr-search" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="חיפוש בעל עניין..." aria-label="חיפוש" />
          </div>
          <button type="button" className="sh-btn sh-btn-primary" onClick={() => setEditing({})}>
            <i className="fi fi-rr-plus" /> הוסף בעל עניין
          </button>
        </div>
      </header>

      {error && <div className="sh-error" role="alert"><i className="fi fi-rr-exclamation" /> {error}</div>}

      {loading && items.length === 0 ? (
        <div className="sh-grid-cards" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => <StakeholderCardSkeleton key={`sk-${i}`} />)}
        </div>
      ) : !loading && filtered.length === 0 ? (
        <div className="sh-empty">
          <i className="fi fi-rr-users" />
          <h2>אין עדיין בעלי עניין</h2>
          <p>הוסיפו יזמים, אדריכלים ויועצים — הם יהיו זמינים לשימוש חוזר בכל פרויקט.</p>
          <button type="button" className="sh-btn sh-btn-primary" onClick={() => setEditing({})}>
            <i className="fi fi-rr-plus" /> הוסף בעל עניין
          </button>
        </div>
      ) : (
        <div className={`sh-groups ${loading ? 'is-loading' : ''}`}>
          {groupStakeholdersByType(filtered).map((g) => (
            <section className="sh-group" key={g.type}>
              <div className="sh-group-head">
                <h2>{g.label}</h2>
                <span className="sh-group-count">{g.items.length}</span>
                {/* Lightweight contextual add — opens the form pre-set to this
                    type (spec 1.8). */}
                <button
                  type="button"
                  className="sh-group-add"
                  onClick={() => setEditing({ seed: { type: g.type } })}
                  aria-label={`הוסף ${g.label}`}
                  title="הוסף לקטגוריה זו"
                >
                  <i className="fi fi-rr-plus" />
                </button>
              </div>
              <div className="sh-grid-cards">
                {g.items.map((s) => (
                  <StakeholderCard key={s.id} s={s} onEdit={setEditing} onDelete={handleDelete} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {editing !== null && (
        <StakeholderModal
          stakeholder={editing && editing.id ? editing : null}
          seed={editing && editing.seed}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}
